import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, Car, CalendarDays, Users, MessageSquare, 
  FileText, Settings, LogOut, Menu, X, ChevronRight, Ban
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Toaster } from '../../components/ui/sonner';
import { cn } from '../../lib/utils';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/vehicles', icon: Car, label: 'Véhicules' },
    { path: '/admin/vehicle-blocks', icon: Ban, label: 'Blocages' },
    { path: '/admin/bookings', icon: CalendarDays, label: 'Réservations' },
    { path: '/admin/customers', icon: Users, label: 'Clients' },
    { path: '/admin/inquiries', icon: MessageSquare, label: 'Demandes' },
    { path: '/admin/settings', icon: Settings, label: 'Paramètres' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex">
      <Toaster position="top-right" richColors />
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-100 transform transition-transform duration-300 lg:transform-none",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-100">
            <Link to="/admin/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary flex items-center justify-center">
                <Car className="w-6 h-6 text-[#181B1F]" strokeWidth={1.5} />
              </div>
              <div>
                <span className="font-serif text-lg text-[#181B1F]">Admin</span>
                <p className="text-gray-400 text-xs">Geneva Rental</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200",
                  isActive(item.path)
                    ? "bg-primary/10 text-primary border-l-2 border-primary"
                    : "text-gray-500 hover:text-[#181B1F] hover:bg-gray-50"
                )}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <span className="text-[#181B1F] text-sm font-medium">
                  {admin?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-[#181B1F] text-sm">{admin?.username}</p>
                <p className="text-gray-400 text-xs">{admin?.role}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-gray-500 hover:text-[#181B1F] hover:bg-gray-50"
            >
              <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="h-16 border-b border-gray-100 flex items-center justify-between px-6">
          <Button
            variant="ghost"
            className="lg:hidden text-[#181B1F]"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>

          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-[#181B1F] transition-colors">
              View Site
            </Link>
            <ChevronRight className="w-4 h-4" />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-[#F9FAFB]/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
